import React, { useState, useEffect, useCallback } from 'react';
import { serviceRequestAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Search, ClipboardList } from 'lucide-react';
import { TYPE_LABEL, TYPE_ICON, STATUS_BADGE, RequestActionModal, GtplChangeBadge } from './ServiceRequestsPage';
import useDarkReady from '../theme/useDarkReady';

// Admin's dedicated view of "customers who want to leave or change their package" — a fixed
// type filter over the same ServiceRequest data as the Service Requests page (which also
// carries reconnection/relocation), reusing its approve/reject modal so the effective-date
// picker and GTPL disconnect-ticket flow behave identically here.
const LOOKUP_TYPES = 'disconnection,package_change';
// DTD (day-based billing) and AMP (company-paid) customers don't follow the calendar-month
// cycle this page is built around, so they're excluded here (2026-09-22 user rule).
const EXCLUDED_DURATION_TYPES = 'dtd,amp';

// House number first (that is how the office reads an address), then the address lines and area.
const customerAddress = (c) => [c?.catv?.houseNumber, c?.address?.line1, c?.address?.line2, c?.address?.area].filter(Boolean).join(', ');

export default function AdminLookupPage() {
  useDarkReady();
  const [requests, setRequests] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeRequest, setActiveRequest] = useState(null);
  const [openInEdit, setOpenInEdit] = useState(false);
  const [filters, setFilters] = useState({ page: 1, status: '', type: '', search: '', month: '', gtplPending: '' });
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, type: filters.type || LOOKUP_TYPES, excludeDurationTypes: EXCLUDED_DURATION_TYPES, limit: 20 };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const { data } = await serviceRequestAPI.getAll(params);
      setRequests(data.data);
      setPagination(data.pagination);
    } catch { toast.error('Failed to load requests'); }
    finally { setLoading(false); }
  }, [filters]);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await serviceRequestAPI.getMonthlySummary({ types: LOOKUP_TYPES, excludeDurationTypes: EXCLUDED_DURATION_TYPES, months: 6 });
      setSummary(data.data);
    } catch { /* summary strip is a convenience; ignore failures */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Admin Lookup</h2>
          <p className="text-muted text-small">Customers who asked to disconnect or change their package — {pagination.total || 0} total</p>
        </div>
      </div>

      {summary && (
        <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
          <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase' }}>
            By month raised
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {summary.months.map(m => (
              <button key={m.month} type="button"
                className={`btn btn-sm ${filters.month === m.month ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setFilters(f => ({ ...f, month: f.month === m.month ? '' : m.month, page: 1 }))}>
                {format(new Date(`${m.month}-01T00:00:00`), 'MMM yyyy')} · {m.total}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="filter-bar">
        <div className="search-input-wrapper">
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search ticket, customer, mobile..."
            value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} />
        </div>
        <input type="month" className="form-control" style={{ width: 150 }} value={filters.month}
          onChange={e => setFilters(f => ({ ...f, month: e.target.value, page: 1 }))} title="Filter by month raised" />
        <select className="form-control" style={{ width: 140 }} value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}>
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="scheduled">Scheduled</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
          <option value="superseded">Superseded</option>
        </select>
        <select className="form-control" style={{ width: 180 }} value={filters.type}
          onChange={e => setFilters(f => ({ ...f, type: e.target.value, page: 1 }))}>
          <option value="">Disconnection + Package Change</option>
          <option value="disconnection">Disconnection Only</option>
          <option value="package_change">Package Change Only</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.83rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={filters.gtplPending === 'true'}
            onChange={e => setFilters(f => ({ ...f, gtplPending: e.target.checked ? 'true' : '', page: 1 }))} />
          GTPL change pending
        </label>
        <button className="btn btn-secondary" onClick={() => setFilters({ page: 1, status: '', type: '', search: '', month: '', gtplPending: '' })}>Clear</button>
      </div>

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : requests.length === 0 ? (
            <div className="empty-state"><ClipboardList size={32} /><h3>No requests found</h3></div>
          ) : (
            <table>
              <thead>
                <tr><th>Ticket</th><th>Customer</th><th>Address</th><th>Type</th><th>Service</th><th>Status</th><th>GTPL</th><th>Raised</th><th>Created</th><th></th></tr>
              </thead>
              <tbody>
                {requests.map(r => {
                  const Icon = TYPE_ICON[r.type] || ClipboardList;
                  return (
                    <tr key={r._id} style={{ cursor: 'pointer' }} onClick={() => { setActiveRequest(r); setOpenInEdit(false); }}>
                      <td className="font-mono" style={{ fontSize: '.78rem' }}>{r.ticketNumber}</td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.customer?.name}</div>
                        <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{r.customer?.mobile}</div>
                      </td>
                      <td style={{ fontSize: '.78rem', maxWidth: 220 }}>
                        {customerAddress(r.customer) || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td style={{ fontSize: '.82rem' }}>
                        <Icon size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {TYPE_LABEL[r.type]}
                      </td>
                      <td style={{ fontSize: '.82rem', textTransform: 'capitalize' }}>{r.serviceType === 'broadband' ? 'Broadband' : r.serviceType === 'catv' ? 'CableTV' : '—'}</td>
                      <td><span className={`badge badge-${STATUS_BADGE[r.status]}`} style={{ textTransform: 'capitalize' }}>{r.status}</span></td>
                      <td><GtplChangeBadge gtplChange={r.gtplChange} /></td>
                      <td style={{ fontSize: '.82rem', textTransform: 'capitalize' }}>{r.raisedBy}</td>
                      <td style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{format(new Date(r.createdAt), 'dd/MM/yy HH:mm')}</td>
                      <td onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setActiveRequest(r); setOpenInEdit(false); }}>View</button>
                        {['pending', 'scheduled', 'completed'].includes(r.status) && (
                          <button className="btn btn-secondary btn-sm" onClick={() => { setActiveRequest(r); setOpenInEdit(true); }}>Edit</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {pagination.pages > 1 && (
          <div className="pagination">
            <button className="page-btn" disabled={!pagination.hasPrev} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>←</button>
            <span style={{ fontSize: '.8rem', padding: '0 8px' }}>{pagination.page} / {pagination.pages}</span>
            <button className="page-btn" disabled={!pagination.hasNext} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>→</button>
          </div>
        )}
      </div>

      {activeRequest && (
        <RequestActionModal request={activeRequest} startEditing={openInEdit} onClose={() => setActiveRequest(null)}
          onSuccess={() => { setActiveRequest(null); load(); loadSummary(); }} />
      )}
    </div>
  );
}
